import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Eye, MessageCircle, Share2, BookmarkPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Post } from '../../store/blogStore';
import { formatRelativeTime } from '../../utils/date';

interface BlogPostProps {
  post: Post;
}

export const BlogPost: React.FC<BlogPostProps> = ({ post }) => {
  return (
    <motion.article 
      className="glass p-6 relative overflow-hidden group"
      whileHover={{ scale: 1.02 }}
    >
      {post.coverImage && (
        <div className="relative h-48 mb-4 rounded-lg overflow-hidden">
          <img 
            src={post.coverImage} 
            alt={post.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
      )}

      <div className="flex items-center space-x-2 mb-3">
        <span className="px-3 py-1 text-xs rounded-full bg-neon-green/20 text-neon-green">
          {post.category}
        </span>
      </div>

      <Link to={`/blog/${post.slug}`}>
        <h2 className="text-2xl font-bold mb-2 group-hover:text-neon-green transition-colors">
          {post.title}
        </h2>
      </Link>

      <p className="text-gray-400 mb-4">
        {post.excerpt}
      </p>

      <div className="flex items-center justify-between text-sm text-gray-400">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span>{formatRelativeTime(post.publishedAt)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Eye className="w-4 h-4" />
            <span>{post.views}</span>
          </div>
          <div className="flex items-center space-x-2">
            <MessageCircle className="w-4 h-4" />
            <span>{post.comments}</span>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button className="p-2 rounded-lg hover:bg-white/5 transition-colors">
            <Share2 className="w-4 h-4" />
          </button>
          <button className="p-2 rounded-lg hover:bg-white/5 transition-colors">
            <BookmarkPlus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center space-x-3 mt-4 pt-4 border-t border-white/10">
        <img 
          src={post.author.avatar} 
          alt={post.author.name}
          className="w-8 h-8 rounded-full"
        />
        <div>
          <div className="font-medium">{post.author.name}</div>
          <div className="text-sm text-gray-400">{post.author.role}</div>
        </div>
      </div>
    </motion.article>
  );
};